import type { KpiRow, ReviewDisagreement } from '../types'

export type ValXmlWorkflowLocalizedText = {
    de: string
    en: string
}

export type ValXmlWorkflowWarning = {
    warning_type: string
    message: ValXmlWorkflowLocalizedText
}

export type ValXmlWorkflowStepStatus = 'pending' | 'running' | 'retrying' | 'completed' | 'not_found' | 'failed' | 'skipped'

export type ValXmlWorkflowProgressStep = {
    id: string
    label: ValXmlWorkflowLocalizedText
    description: ValXmlWorkflowLocalizedText
    status: ValXmlWorkflowStepStatus
    completed_units?: number
    total_units?: number
    unit?: string
}

export type ValXmlWorkflowPartialResult = {
    step_id: string
    revision: number
    value: unknown
}

export type ValXmlWorkflowProgressSnapshot = {
    version: 1
    sequence: number
    steps: ValXmlWorkflowProgressStep[]
    partial_results: ValXmlWorkflowPartialResult[]
    warnings: ValXmlWorkflowWarning[]
}

export type ValXmlWorkflowPdfFile = {
    name: string
    source: {
        sourceType: 'BYTE_CONTENT'
        byteContent: {
            mediaType: 'application/pdf'
            data: string
        }
    }
}

export type ValXmlWorkflowInvoker = (invocation: {
    phase: 'extract' | 'review'
    prompt: string
    files: [ValXmlWorkflowPdfFile]
    onProgress?: (progress: ValXmlWorkflowProgressSnapshot) => void
    onPartialResult?: (partialResult: ValXmlWorkflowPartialResult) => void
}) => Promise<{ response: string }>

export type ValXmlWorkflowKpi = {
    name: string
    value: string
    unit: string | null
    notes: string | null
    page: number | null
    source_text: string | null
    valxml_path: string
    confidence: number
    used_code_interpreter: boolean
}

export type ValXmlWorkflowDisagreement = {
    name: string
    valxml_path: string | null
    reason: string
    value: string | null
    unit: string | null
    notes: string | null
    page: number | null
    source_text: string | null
    confidence: number | null
}

export type ValXmlExtractionWorkflowResult = {
    kpis: ValXmlWorkflowKpi[]
    warnings: ValXmlWorkflowWarning[]
}

export type ValXmlReviewWorkflowResult = {
    disagreements: ValXmlWorkflowDisagreement[]
    warnings: ValXmlWorkflowWarning[]
}

type ValXmlWorkflowSettings = {
    outputLanguage: string
    marketRentTerm: string
    requiredImportDefaultNote: string
}

const buildWorkflowCommand = (operation: 'extract' | 'review', input: Record<string, unknown>) =>
    `einwert-ai-workflow:v1\n${JSON.stringify({ workflow: 'valxml', operation, input })}`

export const buildValXmlExtractCommand = ({ outputLanguage, marketRentTerm, requiredImportDefaultNote }: ValXmlWorkflowSettings) =>
    buildWorkflowCommand('extract', {
        output_language: outputLanguage,
        market_rent_term: marketRentTerm,
        required_import_default_note: requiredImportDefaultNote,
    })

export const buildValXmlReviewCommand = ({
    outputLanguage,
    marketRentTerm,
    currentKpis,
}: Omit<ValXmlWorkflowSettings, 'requiredImportDefaultNote'> & { currentKpis: KpiRow[] }) =>
    buildWorkflowCommand('review', {
        output_language: outputLanguage,
        market_rent_term: marketRentTerm,
        current_kpis: currentKpis.map((kpi) => ({
            name: kpi.name,
            value: kpi.value ?? null,
            unit: kpi.unit ?? null,
            notes: kpi.notes ?? null,
            page: kpi.page ?? null,
            source_text: kpi.sourceText ?? null,
            valxml_path: kpi.valXmlPath ?? null,
            confidence: kpi.confidence ?? null,
            used_code_interpreter: kpi.usedCodeInterpreter ?? false,
        })),
    })

export const toKpiRows = (kpis: ValXmlWorkflowKpi[]): KpiRow[] =>
    kpis.map((kpi) => {
        const hasDirectSource = Boolean(kpi.source_text?.trim())
        return {
            name: kpi.name,
            value: kpi.value,
            unit: kpi.unit ?? undefined,
            notes: kpi.notes ?? undefined,
            page: kpi.page,
            sourceText: hasDirectSource ? (kpi.source_text ?? undefined) : undefined,
            hasDirectSource,
            usedCodeInterpreter: kpi.used_code_interpreter,
            valXmlPath: kpi.valxml_path,
            confidence: kpi.confidence,
            needsReview: false,
            reviewDisagreed: false,
            proposedValue: null,
            proposedMetadata: null,
        }
    })

export const toReviewDisagreements = (disagreements: ValXmlWorkflowDisagreement[]): ReviewDisagreement[] =>
    disagreements.map((disagreement) => ({
        name: disagreement.name,
        valXmlPath: disagreement.valxml_path ?? undefined,
        reason: disagreement.reason,
        value: disagreement.value ?? undefined,
        unit: disagreement.unit ?? undefined,
        notes: disagreement.notes ?? undefined,
        page: disagreement.page,
        sourceText: disagreement.source_text ?? undefined,
        confidence: disagreement.confidence ?? undefined,
    }))
